/**
 * ApprovalModal — accessible modal for human-in-the-loop approval gates.
 *
 * Features:
 *   • Focus trap: Tab/Shift+Tab stay inside the modal
 *   • Escape key closes (calls onDismiss)
 *   • aria-modal, aria-labelledby, aria-describedby
 *   • Approve / Reject buttons with optional notes textarea
 *   • Framer Motion enter/exit animation
 */

import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { ApprovalGate, ApprovalDecision } from '../../api/types'
import { StatusBadge } from './StatusBadge'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ApprovalModalProps {
  gate:        ApprovalGate | null
  /** Whether the modal is currently visible. */
  open:        boolean
  onDismiss:   () => void
  onDecision:  (gateId: string, decision: ApprovalDecision, notes?: string) => void
  /** Loading state while the API call is in-flight. */
  submitting?: boolean
}

// ── Focus trap ────────────────────────────────────────────────────────────────

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

function trapFocus(container: HTMLElement, e: globalThis.KeyboardEvent) {
  const nodes  = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
  if (!nodes.length) return

  const first = nodes[0]
  const last  = nodes[nodes.length - 1]

  if (e.key !== 'Tab') return
  if (e.shiftKey) {
    if (document.activeElement === first) { e.preventDefault(); last.focus() }
  } else {
    if (document.activeElement === last)  { e.preventDefault(); first.focus() }
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ApprovalModal({
  gate,
  open,
  onDismiss,
  onDecision,
  submitting = false,
}: ApprovalModalProps) {
  const overlayRef  = useRef<HTMLDivElement>(null)
  const modalRef    = useRef<HTMLDivElement>(null)
  const notesRef    = useRef<HTMLTextAreaElement>(null)
  const titleId     = 'approval-modal-title'
  const descId      = 'approval-modal-desc'

  // ── Focus trap + Escape key ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return

    // Move focus into modal
    const container = modalRef.current
    if (container) {
      const first = container.querySelector<HTMLElement>(FOCUSABLE)
      first?.focus()
    }

    function handleKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') { onDismiss(); return }
      if (modalRef.current) trapFocus(modalRef.current, e)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onDismiss])

  // ── Backdrop click ───────────────────────────────────────────────────────
  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onDismiss()
  }

  function handleDecision(decision: ApprovalDecision) {
    if (!gate) return
    const notes = notesRef.current?.value.trim() || undefined
    onDecision(gate.id, decision, notes)
  }

  // Render the approval payload as formatted JSON
  const payloadStr = gate?.payload ? JSON.stringify(gate.payload, null, 2) : null

  const BASE_BTN = 'inline-flex items-center gap-1.5 px-5 py-2 rounded-[10px] border text-[13px] font-semibold cursor-pointer transition-[box-shadow,opacity,transform] duration-[150ms] disabled:opacity-[0.45] disabled:cursor-not-allowed active:scale-[0.97]'

  return (
    <AnimatePresence>
      {open && gate && (
        <motion.div
          ref={overlayRef}
          className="fixed inset-0 bg-[rgba(4,8,16,0.65)] backdrop-blur-[4px] flex items-center justify-center z-[300] p-5"
          role="presentation"
          aria-hidden={!open}
          onClick={handleBackdropClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <motion.div
            ref={modalRef}
            className="bg-[rgba(11,18,32,0.72)] backdrop-blur-md border border-[rgba(255,255,255,0.08)] rounded-[22px] shadow-[0_4px_32px_rgba(0,0,0,0.45),0_1px_0_rgba(255,255,255,0.04)_inset,0_0_40px_rgba(168,85,247,0.18)] w-full max-w-[520px] flex flex-col overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 12 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[rgba(148,163,184,0.10)]">
              <div className="flex items-center gap-3">
                <StatusBadge status="waiting_for_approval" />
                <h2 className="text-[15px] font-bold text-[#e2ecf9] m-0" id={titleId}>
                  Approval Required
                </h2>
              </div>
              <button
                className="bg-transparent border-none text-[#4a6280] cursor-pointer text-sm w-7 h-7 flex items-center justify-center rounded-[6px] transition-colors duration-[120ms] hover:bg-[rgba(22,36,58,0.8)] hover:text-[#e2ecf9]"
                aria-label="Close"
                onClick={onDismiss}
              >
                ✕
              </button>
            </div>

            {/* ── Meta ── */}
            <div className="flex flex-col gap-1.5 px-6 py-4 border-b border-[rgba(148,163,184,0.10)]" id={descId}>
              <div className="flex items-baseline gap-2.5">
                <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#4a6280] w-[50px] flex-shrink-0">Run</span>
                <span className="font-mono text-[11px] text-[#8fa3bd] overflow-hidden text-ellipsis whitespace-nowrap">{gate.runId}</span>
              </div>
              <div className="flex items-baseline gap-2.5">
                <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#4a6280] w-[50px] flex-shrink-0">Gate</span>
                <span className="font-mono text-[11px] text-[#8fa3bd] overflow-hidden text-ellipsis whitespace-nowrap">{gate.id}</span>
              </div>
              <div className="flex items-baseline gap-2.5">
                <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#4a6280] w-[50px] flex-shrink-0">Created</span>
                <span className="font-mono text-[11px] text-[#8fa3bd] overflow-hidden text-ellipsis whitespace-nowrap">
                  {new Date(gate.requestedAt).toLocaleString()}
                </span>
              </div>
            </div>

            {/* ── Payload preview ── */}
            {payloadStr && (
              <div className="px-6 py-3.5 border-b border-[rgba(148,163,184,0.10)]">
                <div className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#4a6280] mb-2">Payload</div>
                <pre className="bg-[#101a2b] border border-[rgba(148,163,184,0.10)] rounded-[6px] px-3 py-2.5 font-mono text-[11px] text-[#8fa3bd] overflow-x-auto whitespace-pre max-h-[180px] overflow-y-auto m-0">{payloadStr}</pre>
              </div>
            )}

            {/* ── Notes ── */}
            <div className="flex flex-col gap-2 px-6 py-3.5 border-b border-[rgba(148,163,184,0.10)]">
              <label className="text-xs font-semibold text-[#8fa3bd]" htmlFor="approval-notes">
                Notes <span className="text-[11px] font-normal text-[#4a6280]">(optional)</span>
              </label>
              <textarea
                id="approval-notes"
                ref={notesRef}
                className="bg-[#101a2b] border border-[rgba(148,163,184,0.10)] rounded-[6px] px-3 py-2 font-sans text-xs text-[#e2ecf9] resize-y min-h-[68px] outline-none transition-colors duration-[150ms] focus:border-[#06b6d4] placeholder:text-[#4a6280] w-full"
                placeholder="Add a comment…"
                rows={3}
                disabled={submitting}
              />
            </div>

            {/* ── Actions ── */}
            <div className="flex gap-2.5 justify-end px-6 py-4">
              <button
                className={`${BASE_BTN} bg-[rgba(239,68,68,0.14)] text-[#ef4444] border-[rgba(239,68,68,0.25)] hover:shadow-[0_0_12px_rgba(239,68,68,0.35)] hover:border-[rgba(239,68,68,0.45)]`}
                onClick={() => handleDecision('rejected')}
                disabled={submitting}
                aria-label="Reject this action"
              >
                {submitting ? '…' : '✕ Reject'}
              </button>
              <button
                className={`${BASE_BTN} bg-[rgba(34,197,94,0.14)] text-[#22c55e] border-[rgba(34,197,94,0.25)] hover:shadow-[0_0_12px_rgba(34,197,94,0.35)] hover:border-[rgba(34,197,94,0.45)]`}
                onClick={() => handleDecision('approved')}
                disabled={submitting}
                aria-label="Approve this action"
              >
                {submitting ? '…' : '✓ Approve'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
